/**
 * Document Upload Zone — Drag & drop or file picker for PDF, DOCX, PPTX, Excel.
 * Also supports adding website URLs and cloud storage (Google Drive, OneDrive).
 * Parses documents client-side, fetches websites server-side, and emits extracted text.
 */

import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { type ParsedDocument, parseDocument, validateFile, detectFileType } from './document-parser.js';
import { GoogleDrivePicker, type CloudFileEntry } from './cloud-picker-google.js';
import { OneDrivePicker } from './cloud-picker-onedrive.js';
import { CloudAccountSelector, type CloudAccountInfo } from './cloud-account-selector.js';
import { connectGoogleAccount, connectMicrosoftAccount } from './cloud-account-connect.js';

export interface UploadedFile {
  file: File;
  status: 'parsing' | 'done' | 'error';
  error?: string;
  parsed?: ParsedDocument;
}

export interface WebsiteEntry {
  url: string;
  status: 'fetching' | 'done' | 'error';
  error?: string;
  parsed?: ParsedDocument;
}

/** Snapshot of all upload sources, lifted to the parent so it survives navigation. */
export interface DocumentUploadState {
  files: UploadedFile[];
  websites: WebsiteEntry[];
  cloudFiles: CloudFileEntry[];
}

export interface DocumentUploadProps {
  onDocumentsParsed: (documents: ParsedDocument[]) => void;
  /** Notifies the parent whenever a file/website/cloud import is still being processed. */
  onBusyChange?: (busy: boolean) => void;
  /** Initial upload state used to restore previously fetched files/websites/cloud files. */
  initialState?: DocumentUploadState;
  /** Emits the full upload state whenever it changes so the parent can persist it. */
  onStateChange?: (state: DocumentUploadState) => void;
}

export const DocumentUpload = stateful((props: DocumentUploadProps) => {
  const { onDocumentsParsed } = props;
  const [files, setFiles] = useState<UploadedFile[]>(props.initialState?.files ?? []);
  const [websites, setWebsites] = useState<WebsiteEntry[]>(props.initialState?.websites ?? []);
  const [cloudFiles, setCloudFiles] = useState<CloudFileEntry[]>(props.initialState?.cloudFiles ?? []);
  const [dragging, setDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  // Mirror the full working-set up to the parent so fetched websites/documents
  // are restored when the user navigates back to the Offering step.
  useEffect(() => {
    props.onStateChange?.({ files, websites, cloudFiles });
  }, [files, websites, cloudFiles]);

  // A document/website is still being processed when any source is mid-flight.
  // The wizard uses this to block navigation so in-progress results aren't lost.
  const isBusy =
    files.some(f => f.status === 'parsing') ||
    websites.some(w => w.status === 'fetching') ||
    cloudFiles.some(c => c.status === 'downloading' || c.status === 'parsing');
  useEffect(() => {
    props.onBusyChange?.(isBusy);
  }, [isBusy]);

  // Cloud storage config (fetched once from server)
  const [cloudConfig, setCloudConfig] = useState<{
    google: { clientId: string; apiKey: string; appId: string };
    microsoft: { clientId: string; tenantId: string };
  } | null>(null);

  // Cloud accounts state
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccountInfo[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [microsoftToken, setMicrosoftToken] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  // Fetch cloud config + accounts once after mount
  useEffect(() => {
    Promise.all([
      fetch('/api/config/cloud')
        .then(r => r.json())
        .catch(() => null),
      fetch('/api/cloud-accounts')
        .then(r => r.json())
        .catch(() => ({ accounts: [] })),
    ]).then(([configData, accountsData]) => {
      if (configData) setCloudConfig(configData);
      setCloudAccounts(accountsData?.accounts ?? []);
      setAccountsLoading(false);
    });
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await fetch('/api/cloud-accounts');
      const data = await res.json();
      setCloudAccounts(data.accounts ?? []);
    } catch {
      /* ignore */
    }
  };

  const handleConnectGoogle = async () => {
    if (!cloudConfig?.google?.clientId) return;
    setConnectingProvider('google');
    try {
      const result = await connectGoogleAccount(cloudConfig.google.clientId);
      if (result.ok) {
        await fetchAccounts();
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') return;
      console.error('Google connect error:', err);
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleConnectMicrosoft = async () => {
    if (!cloudConfig?.microsoft?.clientId) return;
    setConnectingProvider('microsoft');
    try {
      const result = await connectMicrosoftAccount(cloudConfig.microsoft.clientId, cloudConfig.microsoft.tenantId);
      console.log('[OneDrive] connect result:', result);
      if (result.ok) {
        await fetchAccounts();
      } else {
        console.error('[OneDrive] connect failed:', result.error);
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') return;
      console.error('[OneDrive] connect error:', err);
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleSelectAccount = async (account: CloudAccountInfo) => {
    try {
      const res = await fetch(`/api/cloud-accounts/${account._id}/token`, { method: 'POST' });
      const data = await res.json();
      if (!data.ok) {
        if (res.status === 401) {
          await fetchAccounts();
        }
        console.error('Token refresh failed:', data.error);
        return;
      }

      if (data.clientSideRefresh) {
        // Microsoft SPA: refresh token exchange must happen in the browser
        const tokenRes = await fetch(`https://login.microsoftonline.com/${data.tenantId}/oauth2/v2.0/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: data.clientId,
            grant_type: 'refresh_token',
            refresh_token: data.refreshToken,
            scope: 'openid offline_access Files.Read.All User.Read',
          }).toString(),
        });
        if (!tokenRes.ok) {
          const err = await tokenRes.json().catch(() => ({}));
          if ((err as any).error === 'invalid_grant' || (err as any).error === 'interaction_required') {
            await fetchAccounts();
            console.error('Microsoft account revoked, please reconnect');
            return;
          }
          console.error('Microsoft token refresh failed:', (err as any).error_description || 'Unknown error');
          return;
        }
        const tokenData = (await tokenRes.json()) as { access_token: string; refresh_token?: string };

        // If Microsoft rotated the refresh token, update on server
        if (tokenData.refresh_token) {
          await fetch(`/api/cloud-accounts/${account._id}/update-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: tokenData.refresh_token }),
          }).catch(() => {});
        }

        setMicrosoftToken(tokenData.access_token);
      } else if (account.provider === 'google_drive') {
        setGoogleToken(data.accessToken);
      } else {
        setMicrosoftToken(data.accessToken);
      }
    } catch (err) {
      console.error('Failed to get access token:', err);
    }
  };

  const handleDisconnect = async (accountId: string) => {
    try {
      await fetch(`/api/cloud-accounts/${accountId}`, { method: 'DELETE' });
      await fetchAccounts();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  /** Emit combined parsed documents from all sources (files + websites + cloud) */
  const emitAll = (updatedFiles: UploadedFile[], updatedWebsites: WebsiteEntry[], updatedCloud?: CloudFileEntry[]) => {
    const fileDocs = updatedFiles.filter(f => f.status === 'done' && f.parsed).map(f => f.parsed!);
    const webDocs = updatedWebsites.filter(w => w.status === 'done' && w.parsed).map(w => w.parsed!);
    const cloudDocs = (updatedCloud ?? cloudFiles).filter(c => c.status === 'done' && c.parsed).map(c => c.parsed!);
    onDocumentsParsed([...fileDocs, ...webDocs, ...cloudDocs]);
  };

  const processFiles = async (fileList: File[]) => {
    const validFiles = fileList.filter(f => detectFileType(f) !== null);
    if (validFiles.length === 0) return;

    // Add to list with 'parsing' status
    const newEntries: UploadedFile[] = validFiles.map(f => ({ file: f, status: 'parsing' as const }));
    const updated = [...files, ...newEntries];
    setFiles(updated);

    // Parse each file (start with original files, push final results to avoid duplicates)
    const results: UploadedFile[] = [...files];
    for (const entry of newEntries) {
      const validationError = validateFile(entry.file);
      if (validationError) {
        results.push({ file: entry.file, status: 'error', error: validationError });
        continue;
      }
      try {
        const parsed = await parseDocument(entry.file);
        if (!parsed.fullText.trim()) {
          results.push({
            file: entry.file,
            status: 'error',
            error: 'No text could be extracted. The file may contain only images.',
          });
        } else {
          results.push({ file: entry.file, status: 'done', parsed });
        }
      } catch (err) {
        results.push({
          file: entry.file,
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to parse file',
        });
      }
      // Update state after each file so UI reflects progress
      setFiles([...results]);
    }

    // Emit all successfully parsed documents (files + websites)
    emitAll(results, websites);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer?.files ?? []);
    if (droppedFiles.length > 0) processFiles(droppedFiles);
  };

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.docx,.pptx,.xlsx,.xls';
    input.multiple = true;
    input.onchange = () => {
      const selected = Array.from(input.files ?? []);
      if (selected.length > 0) processFiles(selected);
    };
    input.click();
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    emitAll(updated, websites);
  };

  const clearAll = () => {
    setFiles([]);
    setWebsites([]);
    setCloudFiles([]);
    onDocumentsParsed([]);
  };

  // ---------------------------------------------------------------------------
  // Website URL handling
  // ---------------------------------------------------------------------------

  const isValidUrl = (str: string): boolean => {
    try {
      const u = new URL(str);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const addUrl = async () => {
    const url = urlInput.trim();
    setUrlError(null);

    if (!url) return;
    if (!isValidUrl(url)) {
      setUrlError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    // Prevent duplicates
    if (websites.some(w => w.url === url)) {
      setUrlError('This URL has already been added');
      return;
    }

    const newEntry: WebsiteEntry = { url, status: 'fetching' };
    const updatedWebsites = [...websites, newEntry];
    setWebsites(updatedWebsites);
    setUrlInput('');

    try {
      const res = await fetch('/api/websites/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: [url] }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Request failed' }));
        const errWebsites = updatedWebsites.map(w =>
          w.url === url ? { ...w, status: 'error' as const, error: data.error || `Server error (${res.status})` } : w,
        );
        setWebsites(errWebsites);
        emitAll(files, errWebsites);
        return;
      }

      const data = (await res.json()) as {
        ok: boolean;
        results: { url: string; title: string; text: string; charCount: number; error?: string }[];
      };
      const result = data.results?.[0];

      if (!result || result.error) {
        const errWebsites = updatedWebsites.map(w =>
          w.url === url ? { ...w, status: 'error' as const, error: result?.error || 'Failed to extract content' } : w,
        );
        setWebsites(errWebsites);
        emitAll(files, errWebsites);
        return;
      }

      // Map to ParsedDocument
      const hostname = new URL(url).hostname;
      const parsed: ParsedDocument = {
        fileName: result.title || hostname,
        fileType: 'website',
        pages: [{ pageNumber: 1, text: result.text }],
        fullText: result.text,
        sourceUrl: url,
      };

      const doneWebsites = updatedWebsites.map(w => (w.url === url ? { ...w, status: 'done' as const, parsed } : w));
      setWebsites(doneWebsites);
      emitAll(files, doneWebsites);
    } catch (err) {
      const errWebsites = updatedWebsites.map(w =>
        w.url === url
          ? { ...w, status: 'error' as const, error: err instanceof Error ? err.message : 'Failed to fetch website' }
          : w,
      );
      setWebsites(errWebsites);
      emitAll(files, errWebsites);
    }
  };

  const removeWebsite = (index: number) => {
    const updated = websites.filter((_, i) => i !== index);
    setWebsites(updated);
    emitAll(files, updated);
  };

  const websiteStatusBadge = (w: WebsiteEntry) => {
    switch (w.status) {
      case 'fetching':
        return html`<span class="badge badge-warning badge-sm gap-1">
          <span class="loading loading-spinner loading-xs"></span> Fetching…
        </span>`;
      case 'done': {
        const chars = w.parsed?.fullText.length ?? 0;
        const label =
          chars < 1000
            ? `${chars} chars`
            : chars < 10_000
              ? `${(chars / 1000).toFixed(1)}k chars`
              : `${Math.round(chars / 1000)}k chars`;
        return html`<span class="badge badge-success badge-sm">${label}</span>`;
      }
      case 'error':
        return html`<span class="badge badge-error badge-sm">Error</span>`;
    }
  };

  const statusBadge = (f: UploadedFile) => {
    switch (f.status) {
      case 'parsing':
        return html`<span class="badge badge-warning badge-sm gap-1">
          <span class="loading loading-spinner loading-xs"></span> Parsing…
        </span>`;
      case 'done': {
        const pageCount = f.parsed?.pages.length ?? 0;
        return html`<span class="badge badge-success badge-sm">${pageCount} page${pageCount === 1 ? '' : 's'}</span>`;
      }
      case 'error':
        return html`<span class="badge badge-error badge-sm">Error</span>`;
    }
  };

  // ---------------------------------------------------------------------------
  // Cloud file handling (Google Drive + OneDrive)
  // ---------------------------------------------------------------------------

  const handleCloudFiles = (entries: CloudFileEntry[]) => {
    // Merge or replace entries by ID
    const updated = [...cloudFiles];
    for (const entry of entries) {
      const existingIdx = updated.findIndex(c => c.id === entry.id);
      if (existingIdx >= 0) {
        updated[existingIdx] = entry;
      } else {
        updated.push(entry);
      }
    }
    setCloudFiles(updated);
    emitAll(files, websites, updated);
  };

  const removeCloudFile = (index: number) => {
    const updated = cloudFiles.filter((_, i) => i !== index);
    setCloudFiles(updated);
    emitAll(files, websites, updated);
  };

  const cloudStatusBadge = (c: CloudFileEntry) => {
    switch (c.status) {
      case 'downloading':
        return html`<span class="badge badge-warning badge-sm gap-1">
          <span class="loading loading-spinner loading-xs"></span> Downloading…
        </span>`;
      case 'parsing':
        return html`<span class="badge badge-warning badge-sm gap-1">
          <span class="loading loading-spinner loading-xs"></span> Parsing…
        </span>`;
      case 'done': {
        const pages = c.parsed?.pages.length ?? 0;
        return html`<span class="badge badge-success badge-sm">${pages} page${pages === 1 ? '' : 's'}</span>`;
      }
      case 'error':
        return html`<span class="badge badge-error badge-sm">Error</span>`;
    }
  };

  return html`
    <div class="flex flex-col gap-3">
      <!-- Drop zone -->
      <div
        class="border-base-300 bg-base-200/30 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          dragging ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
        }"
        @dragover=${(e: DragEvent) => {
          e.preventDefault();
          setDragging(true);
        }}
        @dragleave=${() => setDragging(false)}
        @drop=${handleDrop}
        @click=${handleFileSelect}
      >
        <span class="icon-[material-symbols--upload-file] text-base-content/40 text-4xl"></span>
        <div>
          <span class="text-base-content/70 text-sm">
            Drag & drop your files here, or <span class="text-primary font-medium">browse</span>
          </span>
        </div>
        <span class="text-base-content/40 text-xs">Supported: PDF, DOCX, PPTX, Excel (max 20 MB each)</span>
      </div>

      <!-- Cloud storage pickers -->
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-base-content/60 text-xs">Or import from:</span>
        ${
          cloudConfig?.google?.clientId
            ? CloudAccountSelector({
                provider: 'google_drive',
                accounts: cloudAccounts.filter(a => a.provider === 'google_drive'),
                loading: accountsLoading || connectingProvider === 'google',
                onConnect: handleConnectGoogle,
                onSelect: handleSelectAccount,
                onDisconnect: handleDisconnect,
              })
            : nothing
        }
        ${
          cloudConfig?.microsoft?.clientId
            ? CloudAccountSelector({
                provider: 'onedrive',
                accounts: cloudAccounts.filter(a => a.provider === 'onedrive'),
                loading: accountsLoading || connectingProvider === 'microsoft',
                onConnect: handleConnectMicrosoft,
                onSelect: handleSelectAccount,
                onDisconnect: handleDisconnect,
              })
            : nothing
        }
      </div>

      <!-- Google Drive picker (auto-opens when token is set) -->
      ${
        googleToken && cloudConfig?.google
          ? GoogleDrivePicker({
              googleClientId: cloudConfig.google.clientId,
              googleApiKey: cloudConfig.google.apiKey,
              googleAppId: cloudConfig.google.appId,
              accessToken: googleToken,
              onFilesSelected: handleCloudFiles,
            })
          : nothing
      }

      <!-- OneDrive file browser (auto-opens when token is set) -->
      ${microsoftToken ? OneDrivePicker({ accessToken: microsoftToken, onFilesSelected: handleCloudFiles }) : nothing}

      <!-- File list -->
      ${
        files.length > 0
          ? html`
            <div class="flex flex-col gap-1">
              ${files.map(
                (f, i) => html`
                  <div
                    class="border-base-300 bg-base-200/50 flex items-center gap-2 rounded-lg border px-3 py-2"
                  >
                    <span class="icon-[material-symbols--description] text-base-content/50 text-lg"></span>
                    <span class="flex-1 truncate text-sm" title=${f.file.name}>${f.file.name}</span>
                    ${statusBadge(f)}
                    ${
                      f.status === 'error'
                        ? html`<span class="text-error text-xs" title=${f.error ?? ''}>${f.error}</span>`
                        : nothing
                    }
                    <button
                      class="btn btn-ghost btn-xs btn-square"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        removeFile(i);
                      }}
                    >
                      <span class="icon-[material-symbols--close] text-base"></span>
                    </button>
                  </div>
                `,
              )}
            </div>
          `
          : nothing
      }

      <!-- Website URL input -->
      <div class="flex flex-col gap-2">
        <span class="text-base-content/60 text-xs">Or add a website URL:</span>
        <div class="flex items-center gap-2">
          <div class="flex-1">
            ${TextInput({
              value: urlInput,
              onChanged: (v: string | null) => {
                setUrlInput(v ?? '');
                setUrlError(null);
              },
              onSubmit: () => addUrl(),
              placeholder: 'https://www.yourproduct.com',
            })}
          </div>
          <button class="btn btn-outline btn-sm flex-none" @click=${() => addUrl()}>
            <span class="icon-[material-symbols--language] text-lg"></span>
            Add URL
          </button>
        </div>
        ${urlError ? html`<span class="text-error text-xs">${urlError}</span>` : nothing}
      </div>

      <!-- Website list -->
      ${
        websites.length > 0
          ? html`
            <div class="flex flex-col gap-1">
              ${websites.map(
                (w, i) => html`
                  <div class="border-base-300 bg-base-200/50 flex items-center gap-2 rounded-lg border px-3 py-2">
                    <span class="icon-[material-symbols--language] text-base-content/50 text-lg"></span>
                    <span class="flex-1 truncate text-sm" title=${w.url}>${w.url}</span>
                    ${websiteStatusBadge(w)}
                    ${
                      w.status === 'error'
                        ? html`<span class="text-error text-xs" title=${w.error ?? ''}>${w.error}</span>`
                        : nothing
                    }
                    <button
                      class="btn btn-ghost btn-xs btn-square"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        removeWebsite(i);
                      }}
                    >
                      <span class="icon-[material-symbols--close] text-base"></span>
                    </button>
                  </div>
                `,
              )}
            </div>
          `
          : nothing
      }

      <!-- Cloud files list -->
      ${
        cloudFiles.length > 0
          ? html`
            <div class="flex flex-col gap-1">
              ${cloudFiles.map(
                (c, i) => html`
                  <div class="border-base-300 bg-base-200/50 flex items-center gap-2 rounded-lg border px-3 py-2">
                    <span class="${c.source === 'gdrive' ? 'icon-[mdi--google-drive]' : 'icon-[mdi--microsoft-onedrive]'} text-base-content/50 text-lg"></span>
                    <span class="flex-1 truncate text-sm" title=${c.name}>${c.name}</span>
                    ${cloudStatusBadge(c)}
                    ${
                      c.status === 'error'
                        ? html`<span class="text-error text-xs" title=${c.error ?? ''}>${c.error}</span>`
                        : nothing
                    }
                    <button
                      class="btn btn-ghost btn-xs btn-square"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        removeCloudFile(i);
                      }}
                    >
                      <span class="icon-[material-symbols--close] text-base"></span>
                    </button>
                  </div>
                `,
              )}
            </div>
          `
          : nothing
      }

      <!-- Clear all button -->
      ${
        files.length > 0 || websites.length > 0 || cloudFiles.length > 0
          ? html`
            <div class="flex justify-end">
              <button class="btn btn-ghost btn-xs" @click=${clearAll}>Clear all</button>
            </div>
          `
          : nothing
      }
    </div>
  `;
});
